import React, { useEffect, useCallback, memo, useMemo, type KeyboardEvent } from 'react';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { BulkSelectCheckbox } from '../../../../common/ui/bulk-select-checkbox';
import {
	getAllIssueChildren,
	useBulkSelectionStore,
	useIsGroupSelected,
	useDiffIssuesSelected,
	useSelectCollapsedGroups,
	useShiftKeySelection,
} from '../../../../controllers/bulk-selection';
import { useHierarchyStore } from '../../../../controllers/hierarchy';
import type { IssuesMap } from '../../../../controllers/hierarchy/hierarchy-store';
import type { ListViewControllerData } from '../../../../controllers/list-view-issues/types';
import messages from './messages';

type SelectGroupProps = {
	group: ListViewControllerData;
	rowIndex?: number;
	onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
};

const getFirstParent = ({
	issuesMap,
	issueId,
}: {
	issuesMap: IssuesMap;
	issueId: string;
}): string => {
	const parentIssueId = issuesMap.get(issueId)?.parentIssueId;
	if (parentIssueId) {
		return getFirstParent({ issuesMap, issueId: parentIssueId });
	}
	return issueId;
};

export const SelectGroupCheckbox = memo<SelectGroupProps>(
	({ group, rowIndex, onKeyDown }: SelectGroupProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const [{ issuesMap, expandedItems, childrenItems }] = useHierarchyStore();
		const [
			{ areAllIssuesSelected, selectedIssues },
			{ selectIssues, unselectIssues, selectGroup, unselectGroup },
		] = useBulkSelectionStore();
		const [isGroupSelected] = useIsGroupSelected(group.issueId);
		const { onShiftKeyBulkSelection } = useShiftKeySelection();
		const { formatMessage } = useIntl();

		const { fetchingGroups, selectCollapsedGroups } = useSelectCollapsedGroups();
		const isLoadingGroupIssues = childrenItems[group.issueId]?.loading;

		const groupIssues = useMemo(
			() =>
				!isLoadingGroupIssues ? getAllIssueChildren({ issue: group, childrenItems }) : undefined,
			[childrenItems, group, isLoadingGroupIssues],
		);
		const [diffGroupIssuesSelected] = useDiffIssuesSelected(groupIssues);

		// fetchingGroups - prevent unselecting the group while fetching issues
		useEffect(() => {
			if (
				!areAllIssuesSelected &&
				!fetchingGroups &&
				!isLoadingGroupIssues &&
				diffGroupIssuesSelected &&
				groupIssues?.length
			) {
				if (diffGroupIssuesSelected.length === 0 && !isGroupSelected) {
					// if all the issues of the group are selected we should select the group
					selectGroup(group.issueId);
				} else if (diffGroupIssuesSelected.length && isGroupSelected) {
					// if some issue is not selected we should unselect the group
					unselectGroup(group.issueId);
				}
			}
		}, [
			areAllIssuesSelected,
			diffGroupIssuesSelected,
			fetchingGroups,
			group.issueId,
			groupIssues?.length,
			isGroupSelected,
			isLoadingGroupIssues,
			selectGroup,
			unselectGroup,
		]);

		const hasChildren = group.childSummary?.hasChildren;

		const hasFirstIssuesToFetch = useMemo(
			() => expandedItems[group.issueId] === undefined && hasChildren && groupIssues?.length === 0,
			[expandedItems, group.issueId, groupIssues?.length, hasChildren],
		);

		const onClickCheckbox = useCallback(
			({ isChecked, shiftKeyPressed }: { isChecked: boolean; shiftKeyPressed: boolean }) => {
				fireUIAnalytics(createAnalyticsEvent({}), 'checkbox clicked', 'selectGroup', {
					isChecked,
					rowIndex,
					shiftKeyPressed,
				});

				if (isChecked) {
					// if the group is selected and we have not fetched children of the group
					// we should expand the group and fetch them
					selectCollapsedGroups([group]);
					selectIssues({
						issues: groupIssues || [],
						selectGroup: group.issueId,
						rowIndex,
					});
				} else {
					let issuesToUnselect = groupIssues || [];
					if (!groupIssues?.length) {
						/* Edge case - Sorting - line 170 */
						issuesToUnselect = [...selectedIssues].filter(
							(issueId) => getFirstParent({ issuesMap, issueId }) === group.issueId,
						);
					}

					unselectIssues({
						issues: issuesToUnselect,
						unselectGroup: group.issueId,
						rowIndex,
					});
				}

				if (shiftKeyPressed) {
					onShiftKeyBulkSelection({ isChecked, rowIndex });
				}
			},
			[
				createAnalyticsEvent,
				group,
				groupIssues,
				issuesMap,
				onShiftKeyBulkSelection,
				rowIndex,
				selectCollapsedGroups,
				selectIssues,
				selectedIssues,
				unselectIssues,
			],
		);

		//  if there is some issue of the group selected
		let hasSomeGroupIssueSelected = !!(
			Number(diffGroupIssuesSelected?.length) > 0 &&
			Number(diffGroupIssuesSelected?.length) < Number(groupIssues?.length)
		);

		// shouldBeSelected is to check the group immediately when all the issues are selected (diffGroupIssuesSelected is 0)
		//  instead of waiting for the useEffect (line 73) to update the store to calling selectGroup once all the issues are selected
		const shouldBeSelected =
			Number(diffGroupIssuesSelected?.length) === 0 &&
			hasChildren &&
			Number(groupIssues?.length) > 0 &&
			!hasFirstIssuesToFetch;

		let isChecked = isGroupSelected || hasSomeGroupIssueSelected || !!shouldBeSelected;

		if (!isChecked && selectedIssues.size) {
			/*
                Edge case - Sorting

                When the sorting of the columns or the filters changes all the groups change to collapsed and all the children's data it's null.
                If there is data selected we are not able to match it with the group data.
                If the change it's due to a filter change we clear the selection because we don't know if it's going to match with the new group data.
                If the change it's due to sorting we know that will still be part of that group
            */

			hasSomeGroupIssueSelected = [...selectedIssues].some(
				(issueId) => getFirstParent({ issuesMap, issueId }) === group.issueId,
			);
			isChecked = hasSomeGroupIssueSelected;
		}

		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				if (e.key === 'Enter') {
					onClickCheckbox({ isChecked: !isChecked, shiftKeyPressed: e.shiftKey });
					return;
				}
				onKeyDown?.(e);
			},
			[isChecked, onClickCheckbox, onKeyDown],
		);

		return (
			<BulkSelectCheckbox
				onClick={onClickCheckbox}
				onKeyDown={handleKeyDown}
				isChecked={isChecked}
				isIndeterminate={hasSomeGroupIssueSelected}
				label={formatMessage(messages.selectGroup)}
			/>
		);
	},
);
